import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AimdocJobQuestion } from '@aimdoc/sdk';
import { toast } from 'react-toastify';

import useApi from './api';
import useAnalytics from './analytics/analytics';
import { errorDetail, type McpServer } from './integrations/mcpServerRegistry';
import { useMcpServerDraft } from './integrations/useMcpServerDraft';
import { OnboardingStage, StageButton } from './onboarding/OnboardingStage';
import {
  ConnectProductStep,
  IntentStep,
  PaymentsStep,
  StripeHandoffStep,
  StripeReturnedStep,
  SurfacesStep,
  WebsiteStep,
} from './onboarding/OnboardingSteps';
import { BuildingStep, isRunSettled, type BuildAnswer } from './onboarding/BuildingStep';
import {
  createOnboardingJobsClient,
  progressFromPlan,
  runStatusFromJobStatus,
  runStatusFromWake,
  type ServicesIdentityExchange,
} from '../lib/onboardingJob';
import {
  clearDraft,
  readDraft,
  stepsFor,
  writeDraft,
  type IntentKey,
  type OnboardingState,
  type PaymentsChoice,
  type PersistedDraft,
  type RunSummary,
  type StepKey,
  type StripeSubstep,
  type SurfaceKey,
  productNameFromUrl,
} from './onboarding/types';

const SURFACE_LABELS: Record<SurfaceKey, string> = {
  website: 'Website',
  product: 'Application',
  email: 'Email',
  gateway: 'Gateway',
};

const STEP_COPY: Record<Exclude<StepKey, 'payments'>, { title: string; subtitle: string }> = {
  intent: {
    title: 'What do you want to build with Aimdoc?',
    subtitle: 'This decides what we set up for you in the next few minutes.',
  },
  connect: {
    title: 'Connect your product',
    subtitle: "Aimdoc works through your product's MCP server. Everything we build next comes from what it can actually do.",
  },
  surfaces: {
    title: 'Where should your agent work?',
    subtitle: 'We picked a starting point from your answers. Change it however you like.',
  },
  website: {
    title: 'Confirm your company website',
    subtitle: 'Aimdoc starts building the moment you confirm it.',
  },
  building: {
    title: 'Aimdoc is building your setup',
    subtitle: 'Nothing goes live until you review it.',
  },
};

const PAYMENTS_COPY: Record<StripeSubstep, { title: string; subtitle: string }> = {
  choose: {
    title: 'Get paid for your services',
    subtitle: 'Aimdoc is already building in the background. Connect Stripe now so pricing is ready when your catalog is.',
  },
  handoff: {
    title: 'Set up payments',
    subtitle: "Stripe takes it from here while we keep building. Come back to this tab when you're finished.",
  },
  returned: {
    title: 'Payments are almost ready',
    subtitle: "You're back from Stripe. Here's where things stand; your setup kept building meanwhile.",
  },
};

type Props = {
  user?: { email?: string } | null;
  organization?: { name?: string } | null;
  initialWebsiteUrl?: string | null;
  /** Server state from UserContext; the flow resumes from it. */
  state?: OnboardingState | null;
  /** Called with every fresh server state (intents saved, submitted, completed). */
  onStateChange?: (state: OnboardingState) => void;
  /** Kept for callers that only care about the submit moment. */
  onSubmitted?: (state: OnboardingState) => void;
  /** The user clicked through to the dashboard (status is now completed). */
  onCompleted?: (state: OnboardingState) => void;
};

const inferWebsite = (email?: string, provided?: string | null) => {
  if (provided) return provided;
  const domain = email?.split('@')[1]?.trim();
  return domain ? `https://${domain}` : 'https://';
};

const makeSubmissionId = () =>
  globalThis.crypto?.randomUUID?.() ?? `onboarding-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const hostOf = (url: string) => {
  try {
    return new URL(url.includes('://') ? url : `https://${url}`).hostname;
  } catch {
    return url;
  }
};

const POLL_MS = 3000;

/**
 * New-organization onboarding (ONBOARDING_FLOW_PLAN.md): intent → connect
 * product (org MCP) → surfaces → website → get paid (Stripe Connect) →
 * building. Confirming the website submits and starts the onboarding job
 * (OWNED_JOBS_PLAN.md); the Stripe step then runs while it builds, and the
 * build screen follows the job's setup wake over `jobs.subscribe` until the
 * user goes to the dashboard.
 */
export default function AgentFromAI({
  user,
  organization,
  initialWebsiteUrl,
  state,
  onStateChange,
  onSubmitted,
  onCompleted,
}: Props) {
  const { get, post } = useApi();
  const { captureEvent } = useAnalytics();
  const onboardingId = state?.onboarding_id ?? null;

  const [serverState, setServerState] = useState<OnboardingState | null>(state ?? null);
  useEffect(() => {
    if (state) setServerState(state);
  }, [state]);

  // ---- resumable draft -----------------------------------------------------
  const persisted = useMemo(() => (onboardingId ? readDraft(onboardingId) : null), [onboardingId]);
  const returningFromStripe = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('stripe') === 'return' && params.get('onboarding') === '1';
  }, []);
  // Back from an MCP OAuth sign-in: the backend callback created/updated the
  // server row and redirected here with the result.
  const oauthReturn = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('mcp_oauth');
    if (!result || params.get('onboarding') !== '1') return null;
    return { result, serverId: params.get('mcp_server_id'), message: params.get('message') };
  }, []);

  const [intents, setIntents] = useState<IntentKey[]>(persisted?.intents ?? state?.intents ?? []);
  const [surfaces, setSurfaces] = useState<SurfaceKey[]>(persisted?.surfaces ?? []);
  const [mcpServer, setMcpServer] = useState<McpServer | null>(state?.mcp_server ?? null);
  const serverPayments = (state?.answers?.payments as PaymentsChoice | undefined) ?? null;
  const [payments, setPayments] = useState<PaymentsChoice>(
    serverPayments && serverPayments !== 'pending' ? serverPayments : persisted?.payments ?? 'not_applicable'
  );
  const [stripeSubstep, setStripeSubstep] = useState<StripeSubstep>(
    returningFromStripe ? 'returned' : persisted?.stripeSubstep ?? 'choose'
  );
  const [websiteUrl, setWebsiteUrl] = useState(
    persisted?.websiteUrl || inferWebsite(user?.email, initialWebsiteUrl)
  );
  const submissionIdRef = useRef(persisted?.submissionId ?? makeSubmissionId());

  const building = Boolean(serverState?.building);
  // Submitted with the services intent but the Get paid step not answered
  // yet: the runs are already going, the wizard still owes this screen.
  const paymentsPending =
    building &&
    (serverState?.intents ?? []).includes('services') &&
    (serverState?.answers?.payments as string | undefined) === 'pending';
  // Once the row is building, the server state is the authority on what the
  // user chose. Local state (or a stale draft, which wins the `??` at mount
  // even when its intents are empty) can be lost on a remount — and an empty
  // intent list blanks the build screen and drops the conditional rail steps.
  const effectiveIntents = useMemo(() => {
    const serverIntents = serverState?.intents ?? [];
    return building && serverIntents.length > 0 ? serverIntents : intents;
  }, [building, serverState?.intents, intents]);
  const steps = useMemo(() => stepsFor(effectiveIntents), [effectiveIntents]);
  const [stepKey, setStepKey] = useState<StepKey>(() => {
    if (returningFromStripe) return 'payments';
    if (state?.building) {
      const pending =
        state.intents.includes('services') && (state.answers?.payments as string | undefined) === 'pending';
      return pending ? 'payments' : 'building';
    }
    if (oauthReturn) return 'connect';
    return persisted?.stepKey ?? 'intent';
  });
  useEffect(() => {
    // Once the row is building, the only screens left are Get paid (if
    // still owed) and the build screen. Leaving the payments step is an
    // explicit action (choose "Not yet" / continue from Stripe), so never
    // pull the user off it here.
    if (!building || stepKey === 'payments') return;
    setStepKey(paymentsPending ? 'payments' : 'building');
  }, [building, paymentsPending, stepKey]);
  // A step can disappear when intents change (e.g. deselect 'agent' after
  // visiting surfaces); snap back to the nearest valid one.
  useEffect(() => {
    if (!building && !steps.includes(stepKey)) setStepKey('intent');
  }, [building, steps, stepKey]);

  const stepIndex = Math.max(0, steps.indexOf(stepKey));
  // Monotonic position for the stage's transition direction: step index with
  // the Stripe sub-steps folded in so moving between them animates forward.
  const flatPosition = useMemo(() => {
    const sub: Record<StripeSubstep, number> = { choose: 0, handoff: 1, returned: 2 };
    return stepIndex * 10 + (stepKey === 'payments' ? sub[stripeSubstep] : 0);
  }, [stepIndex, stepKey, stripeSubstep]);
  const transitionKey = stepKey === 'payments' ? `payments-${stripeSubstep}` : stepKey;

  useEffect(() => {
    if (!onboardingId || building) return;
    const draft: PersistedDraft = {
      stepKey,
      intents,
      surfaces,
      mcpServerId: mcpServer?.id ?? null,
      payments,
      stripeSubstep,
      websiteUrl,
      submissionId: submissionIdRef.current,
    };
    writeDraft(onboardingId, draft);
  }, [onboardingId, building, stepKey, intents, surfaces, mcpServer, payments, stripeSubstep, websiteUrl]);

  // The Aimdoc widget stays hidden for the whole flow (Main owns that), so
  // there is no alternate path around onboarding; the handoff conversation
  // opens once the user reaches the dashboard.

  const applyState = useCallback(
    (next: OnboardingState) => {
      setServerState(next);
      if (next.mcp_server) setMcpServer(next.mcp_server);
      onStateChange?.(next);
    },
    [onStateChange]
  );

  // ---- step 1: intents -----------------------------------------------------
  const [savingIntents, setSavingIntents] = useState(false);
  const toggleIntent = (key: IntentKey) =>
    setIntents((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));

  const leaveIntent = async () => {
    setSavingIntents(true);
    try {
      const response = await post('/organization-onboarding/intents', { intents });
      applyState(response.data as OnboardingState);
      if (!intents.includes('services')) setPayments('not_applicable');
      setStepKey('connect');
    } catch (error) {
      toast.error(errorDetail(error, 'Could not save your choice. Please try again.'));
    } finally {
      setSavingIntents(false);
    }
  };

  // ---- step 2: connect product --------------------------------------------
  const mcpDraft = useMcpServerDraft(null);
  useEffect(() => {
    // Sign-in is the recommended path in onboarding; the dialog keeps 'none'.
    mcpDraft.updateConnectionDetails({ auth_mode: 'oauth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [oauthError, setOauthError] = useState<string | null>(
    oauthReturn?.result === 'error' ? oauthReturn.message || 'Sign-in to your MCP server did not complete.' : null
  );
  // True from the moment we land back from the vendor's sign-in until the
  // server row is loaded: the step renders a settled "finishing sign-in"
  // card instead of an empty form that pops into a connected one.
  const [resolvingOauth, setResolvingOauth] = useState<boolean>(
    Boolean(oauthReturn && oauthReturn.result === 'connected' && oauthReturn.serverId) ||
      Boolean(!state?.mcp_server && persisted?.mcpServerId)
  );
  // A reload between connecting and submitting must not lose the connection:
  // the draft remembers the server id, so load the row back into state.
  useEffect(() => {
    if (oauthReturn || mcpServer || !persisted?.mcpServerId) return;
    (async () => {
      try {
        const response = await get('/mcp-servers');
        const rows = (Array.isArray(response.data) ? response.data : []) as McpServer[];
        const restored = rows.find((row) => row.id === persisted.mcpServerId) ?? null;
        if (restored) setMcpServer(restored);
      } catch {
        // fall through: the step shows the empty form and the user reconnects
      } finally {
        setResolvingOauth(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!oauthReturn) return;
    const finish = () => {
      const url = new URL(window.location.href);
      ['mcp_oauth', 'mcp_server_id', 'message', 'onboarding'].forEach((key) => url.searchParams.delete(key));
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    };
    if (oauthReturn.result !== 'connected' || !oauthReturn.serverId) {
      finish();
      return;
    }
    (async () => {
      try {
        const response = await get('/mcp-servers');
        const rows = (Array.isArray(response.data) ? response.data : []) as McpServer[];
        const connected = rows.find((row) => row.id === oauthReturn.serverId) ?? null;
        if (connected) {
          setMcpServer(connected);
          captureEvent('organization_onboarding_mcp_connected', { tools: connected.tools?.length ?? 0, auth_mode: 'oauth' });
        } else {
          setOauthError('Signed in, but the connection could not be loaded. Refresh to try again.');
        }
      } catch (error) {
        setOauthError(errorDetail(error, 'Signed in, but the connection could not be loaded.'));
      } finally {
        setResolvingOauth(false);
        finish();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthReturn]);

  const signInToProduct = async () => {
    if (onboardingId) {
      // Persist before leaving the page so the return lands back on this step.
      writeDraft(onboardingId, {
        stepKey: 'connect',
        intents,
        surfaces,
        mcpServerId: null,
        payments,
        stripeSubstep,
        websiteUrl,
        submissionId: submissionIdRef.current,
      });
    }
    setOauthError(null);
    await mcpDraft.startOAuth('onboarding');
  };

  const leaveConnect = async () => {
    if (mcpServer) {
      goTo(steps[stepIndex + 1]);
      return;
    }
    if (mcpDraft.draft.auth_mode === 'oauth') {
      await signInToProduct();
      return;
    }
    const saved = await mcpDraft.save();
    if (!saved) return;
    setMcpServer(saved);
    captureEvent('organization_onboarding_mcp_connected', { tools: saved.tools?.length ?? 0 });
    goTo(steps[stepIndex + 1]);
  };
  const skipConnect = () => {
    setMcpServer(null);
    goTo(steps[stepIndex + 1]);
  };

  // ---- step 3: surfaces ----------------------------------------------------
  const toggleSurface = (key: SurfaceKey) =>
    setSurfaces((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));
  useEffect(() => {
    // Suggest a starting point once, the first time surfaces become relevant.
    if (intents.includes('agent') && surfaces.length === 0 && stepKey === 'surfaces') {
      setSurfaces(mcpServer ? ['website', 'product'] : ['website']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  // ---- step 4: payments / Stripe ------------------------------------------
  const [startingStripe, setStartingStripe] = useState(false);
  const [syncingStripe, setSyncingStripe] = useState(false);
  const stripeAccount = serverState?.stripe?.account ?? null;

  const syncStripe = useCallback(
    async (quiet: boolean) => {
      setSyncingStripe(true);
      try {
        await post('/services/commerce/stripe/sync', {});
        const response = await get('/organization-onboarding');
        applyState(response.data as OnboardingState);
        if (!quiet) toast.success('Stripe status refreshed');
      } catch (error) {
        if (!quiet) toast.error(errorDetail(error, 'Could not refresh Stripe status.'));
      } finally {
        setSyncingStripe(false);
      }
    },
    [applyState, get, post]
  );

  useEffect(() => {
    if (!returningFromStripe) return;
    setPayments('stripe');
    setStripeSubstep('returned');
    syncStripe(true).finally(() => {
      // Drop the return marker so a reload does not re-sync forever.
      const url = new URL(window.location.href);
      url.searchParams.delete('stripe');
      url.searchParams.delete('onboarding');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returningFromStripe]);

  /** Get paid is answered after submit: persist it on the (already building) row. */
  const [savingPayments, setSavingPayments] = useState(false);
  const savePayments = async (choice: 'stripe' | 'later') => {
    const response = await post('/organization-onboarding/payments', { payments: choice });
    applyState(response.data as OnboardingState);
  };
  const skipPayments = async () => {
    setSavingPayments(true);
    try {
      await savePayments('later');
      setPayments('later');
      setStepKey('building');
    } catch (error) {
      toast.error(errorDetail(error, 'Could not save your choice. Please try again.'));
    } finally {
      setSavingPayments(false);
    }
  };
  const leaveStripeReturn = () => {
    setStepKey('building');
  };

  const openStripe = async () => {
    setStartingStripe(true);
    try {
      // Persist before leaving the page so the return lands on 4b.
      if (onboardingId) {
        writeDraft(onboardingId, {
          stepKey: 'payments',
          intents,
          surfaces,
          mcpServerId: mcpServer?.id ?? null,
          payments: 'stripe',
          stripeSubstep: 'returned',
          websiteUrl,
          submissionId: submissionIdRef.current,
        });
      }
      // The row is already building; record the decision first so a reload
      // mid-Stripe never drops the user back to "choose".
      await savePayments('stripe');
      const response = await post('/services/commerce/stripe/onboarding', { country: 'US', return_to: 'onboarding' });
      const url = response.data?.url as string | undefined;
      if (!url) throw new Error('Stripe did not return an onboarding link.');
      window.location.assign(url);
    } catch (error) {
      setStartingStripe(false);
      toast.error(errorDetail(error, 'Stripe could not start onboarding. Please try again.'));
    }
  };

  // ---- navigation ----------------------------------------------------------
  const goTo = (key: StepKey | undefined) => {
    if (key) setStepKey(key);
  };
  const goBack = () => {
    if (stepKey === 'payments' && stripeSubstep !== 'choose') {
      setStripeSubstep('choose');
      return;
    }
    goTo(steps[stepIndex - 1]);
  };

  // ---- submit --------------------------------------------------------------
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    try {
      const parsed = new URL(websiteUrl.includes('://') ? websiteUrl : `https://${websiteUrl}`);
      if (!parsed.hostname || !parsed.hostname.includes('.')) throw new Error('missing hostname');
    } catch {
      toast.error('Enter a valid company website.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await post('/organization-onboarding', {
        submission_id: submissionIdRef.current,
        intents,
        ...(intents.includes('agent') ? { surfaces } : {}),
        mcp_server_id: mcpServer?.id ?? null,
        payments: intents.includes('services') ? payments : 'not_applicable',
        website_url: websiteUrl,
      });
      const next = response.data as OnboardingState;
      captureEvent('organization_onboarding_submitted', {
        onboarding_id: next.onboarding_id,
        intents,
        surfaces,
        mcp_connected: Boolean(mcpServer),
        payments,
      });
      applyState(next);
      onSubmitted?.(next);
      // Runs are going. With the services intent, Stripe is next and runs
      // in parallel with the build; otherwise straight to the build screen.
      setStepKey(intents.includes('services') ? 'payments' : 'building');
    } catch (error) {
      toast.error(errorDetail(error, 'Unable to start your setup. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  // ---- building: follow the onboarding job over the SDK, polling only as fallback
  const run = serverState?.runs?.job ?? null;
  const jobId = serverState?.job_id ?? run?.job_id ?? null;
  const allSettled = isRunSettled(run);
  const [streamFallback, setStreamFallback] = useState(false);
  const [question, setQuestion] = useState<AimdocJobQuestion | null>(null);
  const [answering, setAnswering] = useState(false);
  const eventCursorRef = useRef(0);
  const servicesApiUrl = (import.meta.env.VITE_API_URL as string) || '';
  // The dashboard user as an SDK subject: the same public client a
  // customer's product uses, fed one-time assertions from our own API.
  const getRef = useRef(get);
  getRef.current = get;
  const jobsClient = useMemo(
    () =>
      createOnboardingJobsClient(servicesApiUrl, async () => {
        const response = await getRef.current('/users/me/aimdoc-services-identity');
        return response.data as ServicesIdentityExchange;
      }),
    [servicesApiUrl]
  );

  const refreshBuildingState = useCallback(async () => {
    const response = await get('/organization-onboarding');
    applyState(response.data as OnboardingState);
  }, [applyState, get]);

  const patchRun = useCallback((patch: Partial<RunSummary> | ((current: RunSummary) => RunSummary)) => {
    setServerState((current) => {
      const existing = current?.runs?.job;
      if (!current || !existing) return current;
      const next = typeof patch === 'function' ? patch(existing) : { ...existing, ...patch };
      return { ...current, runs: { ...current.runs, job: next } };
    });
  }, []);

  useEffect(() => {
    if (stepKey !== 'building' || allSettled || !jobId) {
      setStreamFallback(false);
      return;
    }
    let cancelled = false;
    setStreamFallback(false);

    // The job can pause on a setup question for the owner; the question
    // itself only rides the job view, so read it when the stream says so.
    const loadQuestion = async () => {
      try {
        const view = await jobsClient.jobs.get(jobId);
        if (!cancelled) setQuestion(view.open_questions[0] ?? null);
      } catch {
        // The "waiting for your answer" state still shows; the next frame retries.
      }
    };

    const unsubscribe = jobsClient.jobs.subscribe(
      jobId,
      {
        onPlan: (plan) => {
          const progress = progressFromPlan(plan);
          if (progress) patchRun({ progress });
        },
        onStatus: (frame) => {
          const status = runStatusFromJobStatus(frame);
          patchRun((current) => ({
            ...current,
            ...(status ? { status } : {}),
            headline: frame.headline ?? current.headline,
          }));
          if (frame.blocked_on === 'owner_input') void loadQuestion();
        },
        onWake: (frame) => {
          const status = runStatusFromWake(frame.status);
          if (!status) return;
          patchRun({ status });
          if (status === 'waiting') void loadQuestion();
          // Terminal: the server reconciles ready_for_review / failed.
          if (status === 'completed' || status === 'failed' || status === 'cancelled') void refreshBuildingState();
        },
        onState: (frame) => {
          if (frame.headline) patchRun({ headline: frame.headline });
        },
        onEvent: (event) => {
          eventCursorRef.current = event.seq;
          if (event.kind === 'asked_owner') void loadQuestion();
        },
        onQuestion: (asked) => {
          if (asked.status === 'open') setQuestion(asked);
          else setQuestion((current) => (current?.id === asked.id ? null : current));
        },
        onOpen: ({ reconnect }) => {
          if (reconnect) void refreshBuildingState();
        },
        onError: () => {
          // The SDK keeps reconnecting; poll alongside so the screen never stalls.
          if (!cancelled) setStreamFallback(true);
        },
      },
      { after: eventCursorRef.current }
    );
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [stepKey, allSettled, jobId, jobsClient, patchRun, refreshBuildingState]);

  const answerQuestion = useCallback(
    async (answer: BuildAnswer) => {
      if (!jobId || !question) return;
      setAnswering(true);
      try {
        await jobsClient.jobs.answer(jobId, question.id, answer);
        setQuestion(null);
        // The job wakes again on the answer; show it as working right away.
        patchRun((current) => (current.status === 'waiting' ? { ...current, status: 'running' } : current));
      } catch (error) {
        toast.error(errorDetail(error, 'Could not send your answer. Please try again.'));
      } finally {
        setAnswering(false);
      }
    },
    [jobId, question, jobsClient, patchRun]
  );

  useEffect(() => {
    if (stepKey !== 'building' || allSettled || !streamFallback) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const response = await get('/organization-onboarding');
        if (!cancelled) applyState(response.data as OnboardingState);
      } catch {
        // Transient; the fallback retries on the next tick.
      }
    };
    void tick();
    const timer = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [stepKey, allSettled, streamFallback, get, applyState]);

  const [completing, setCompleting] = useState(false);
  const complete = async () => {
    setCompleting(true);
    try {
      const response = await post('/organization-onboarding/complete', {});
      const next = response.data as OnboardingState;
      if (onboardingId) clearDraft(onboardingId);
      captureEvent('organization_onboarding_completed', { onboarding_id: next.onboarding_id, all_settled: allSettled });
      applyState(next);
      onCompleted?.(next);
    } catch (error) {
      toast.error(errorDetail(error, 'Could not open the dashboard. Please try again.'));
      setCompleting(false);
    }
  };

  // ---- render --------------------------------------------------------------
  const continueButton = (label: string, onClick: () => void, opts?: { disabled?: boolean; busy?: boolean; icon?: 'arrow' | 'ext' | 'spark' }) => (
    <StageButton onClick={onClick} disabled={opts?.disabled} busy={opts?.busy} icon={opts?.icon ?? 'arrow'}>
      {label}
    </StageButton>
  );

  // Never the placeholder org name (it is derived from the signup email):
  // prefer the product name behind the MCP URL, then the website domain.
  const orgName =
    productNameFromUrl(mcpServer?.url || mcpDraft.draft.url) || productNameFromUrl(websiteUrl) || organization?.name?.trim() || 'your company';
  if (stepKey === 'building') {
    const servicesSkipped = effectiveIntents.includes('services') && !(serverState?.mcp_server ?? mcpServer);
    return (
      <OnboardingStage
        steps={steps}
        current={stepKey}
        position={flatPosition}
        transitionKey={transitionKey}
        fill
        wide
        title={allSettled ? 'Your setup is ready to review' : STEP_COPY.building.title}
        subtitle={
          allSettled
            ? 'Nothing is live yet. Look over what Aimdoc built, then take it from there.'
            : run?.status === 'waiting'
              ? 'Aimdoc has a question for you before it can keep going.'
              : STEP_COPY.building.subtitle
        }
        action={
          allSettled
            ? continueButton('Go to dashboard', complete, { busy: completing })
            : (
              <StageButton tone="quiet" onClick={complete} busy={completing}>
                Skip to the dashboard
              </StageButton>
            )
        }
        actionKey={allSettled ? 'go' : 'skip'}
        footerNote={
          servicesSkipped
            ? 'Services need an MCP server to build from. Connect one under Integrations and we will draft them then.'
            : undefined
        }
      >
        <BuildingStep
          run={run}
          question={question}
          answering={answering}
          onAnswer={answerQuestion}
          mcpName={(serverState?.mcp_server ?? mcpServer)?.name ?? null}
          websiteHost={hostOf((serverState?.answers?.website_url as string | undefined) ?? websiteUrl)}
          surfaceLabels={((serverState?.answers?.surfaces as SurfaceKey[] | undefined) ?? surfaces).map((key) => SURFACE_LABELS[key] ?? key)}
          email={user?.email ?? null}
        />
      </OnboardingStage>
    );
  }

  if (stepKey === 'payments') {
    const copy = PAYMENTS_COPY[stripeSubstep];
    let action;
    if (stripeSubstep === 'choose') {
      action =
        payments === 'stripe'
          ? continueButton('Continue to Stripe', () => setStripeSubstep('handoff'), { icon: 'ext' })
          : continueButton('Start building', skipPayments, {
              disabled: payments !== 'later',
              busy: savingPayments,
              icon: 'spark',
            });
    } else if (stripeSubstep === 'handoff') {
      action = continueButton('Open Stripe', openStripe, { busy: startingStripe, icon: 'ext' });
    } else {
      action = continueButton('See your build', leaveStripeReturn);
    }
    return (
      <OnboardingStage
        steps={steps}
        current={stepKey}
        position={flatPosition}
        transitionKey={transitionKey}
        medium={stripeSubstep === 'choose'}
        title={copy.title}
        subtitle={copy.subtitle}
        // Submit already happened: the only way back is between Stripe substeps.
        onBack={stripeSubstep === 'handoff' ? goBack : undefined}
        backDisabled={startingStripe}
        action={action}
        actionKey={`${stripeSubstep}-${payments}`}
        footerNote={
          stripeSubstep === 'choose'
            ? "A 5% platform fee applies to successful payments. Stripe's processing fees are yours, as with any Stripe account."
            : undefined
        }
      >
        {stripeSubstep === 'choose' && <PaymentsStep choice={payments} onChoose={setPayments} />}
        {stripeSubstep === 'handoff' && <StripeHandoffStep organizationName={orgName} />}
        {stripeSubstep === 'returned' && (
          <StripeReturnedStep
            account={stripeAccount}
            syncing={syncingStripe}
            onRefresh={() => syncStripe(false)}
            onContinueOnboarding={openStripe}
          />
        )}
      </OnboardingStage>
    );
  }

  const copy = STEP_COPY[stepKey];
  let action;
  let secondaryAction;
  let body;
  switch (stepKey) {
    case 'intent':
      action = continueButton('Continue', leaveIntent, { disabled: intents.length === 0, busy: savingIntents });
      body = <IntentStep intents={intents} onToggle={toggleIntent} />;
      break;
    case 'connect': {
      const needsSignIn = !mcpServer && mcpDraft.draft.auth_mode === 'oauth';
      const inferredName = productNameFromUrl(mcpDraft.draft.url);
      action = continueButton(needsSignIn ? `Sign in with ${inferredName || 'your product'}` : 'Continue', leaveConnect, {
        busy: mcpDraft.saving || mcpDraft.startingOAuth || resolvingOauth,
        disabled: (!mcpServer && !inferredName) || resolvingOauth,
        icon: needsSignIn ? 'ext' : 'arrow',
      });
      if (!mcpServer) {
        secondaryAction = (
          <StageButton
            tone="quiet"
            icon={null}
            onClick={skipConnect}
            disabled={mcpDraft.saving || mcpDraft.startingOAuth || resolvingOauth}
          >
            Continue without one
          </StageButton>
        );
      }
      body = (
        <ConnectProductStep
          server={mcpServer}
          draftApi={mcpDraft}
          oauthError={oauthError}
          connecting={resolvingOauth}
        />
      );
      break;
    }
    case 'surfaces':
      action = continueButton('Continue', () => goTo(steps[stepIndex + 1]), { disabled: surfaces.length === 0 });
      body = <SurfacesStep surfaces={surfaces} onToggle={toggleSurface} hasMcp={Boolean(mcpServer)} />;
      break;
    case 'website':
      // The runs start here either way; with the services intent the Get
      // paid step still follows, so the CTA reads as a step, not the finish.
      action = intents.includes('services')
        ? continueButton('Continue', submit, { busy: submitting })
        : continueButton('Start building', submit, { busy: submitting, icon: 'spark' });
      body = <WebsiteStep value={websiteUrl} onChange={setWebsiteUrl} />;
      break;
  }

  return (
    <OnboardingStage
      steps={steps}
      current={stepKey}
      position={flatPosition}
      transitionKey={transitionKey}
      medium={stepKey === 'intent'}
      title={copy.title}
      subtitle={copy.subtitle}
      onBack={stepIndex > 0 ? goBack : undefined}
      backDisabled={savingIntents || submitting || mcpDraft.saving || mcpDraft.startingOAuth}
      action={action}
      secondaryAction={secondaryAction}
      actionKey={stepKey}
    >
      {body}
    </OnboardingStage>
  );
}
